import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  MapSearchData,
  DashboardFilter,
  DashboardFilterAlertResponse,
  DashboardFilterRequest,
  DashboardFilterResult,
  KeywordSearchRecorder,
  MarkerDataSystem,
  AssigneeInfo,
} from "api/interfaces/dashboardInterface.interface";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  MapFilter,
  mapFilterInitialState,
  MapFilterKeyword,
  setAlerts,
  setInitialState,
  setKeyword,
  setRecorders,
  setTypes,
} from "redux/reducers/map/mapFilter";
import { GoogleMapPresenter } from "./GoogleMapPresenter";

import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { postFilterSearchRecorders } from "api/dashboardAPI";
import { debounce, throttle } from "lodash";
import { useQuery, useQueryClient } from "react-query";
import { More } from "./sub/subheader/SubHeader";
import {
  AlertDetail,
  AlertViewInfo,
} from "api/interfaces/alertInterface.interface";
import { checkingMergedSystem } from "utils/DashboardUtil";
import { Theme } from "redux/reducers/theme/themeReducer";
import { useMediaQuery } from "react-responsive";
import RecorderListInfoView from "./sub/infoView/RecorderListInfoView";
import RecorderInfoView from "./sub/infoView/RecorderInfoView";
import { ThemeProvider } from "styled-components";
import { dark, light } from "styles/theme";
import { Provider } from "react-redux";
import { store } from "redux/store";
import { IntlProvider } from "react-intl";
import { QueryClientProvider } from "react-query";
import { createRoot } from "react-dom/client";
import { useAdvancedMarkers } from "hook/useAdvancedMarkers";
import { mapOptions } from "./GoogleMapConfig";
import { selectMapStomp } from "redux/stomp/stompSelector";
import { oneHourInMs } from "utils/timeUtil";
import {
  mapSearchDataSelector,
  markerDataSelector,
  setMapBounds,
  setMapSearchOriginData,
} from "redux/stomp/stompMapSlice";
import { getSpectrumCloud } from "api/recorderAPI";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import * as mediaQuery from "components/MediaQuery";

export interface AlertStatusCountType {
  warningCount: number;
  alertCount: number;
  // assignCount: number;
  criticalCount: number;
}

export interface clusterAlert {
  alertStatusCount: AlertStatusCountType;
  alertType: AlertDetail;
}

export type CustomMarker = google.maps.marker.AdvancedMarkerElement & {
  data?: MarkerDataType;
  _diff?: string;
};

export interface MarkerDataType {
  recorderIds: string;
  mergedSystemId: string;
  title: string;
  latitude: number;
  longitude: number;
  groupingSystems: MarkerDataSystem[];
  alertStatusCount: AlertStatusCountType;
  isAssignee: AssigneeInfo;
}

export default function GoogleMapContainer(): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const markers: MarkerDataType[] = useAppSelector(markerDataSelector);
  const currentMapSearchData: MapSearchData[] = useAppSelector(
    mapSearchDataSelector
  );

  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const mapFilter: MapFilter = useAppSelector((state) => state.mapFilter);

  const theme: Theme = useAppSelector((state) => state.theme);
  const canHover = useMediaQuery({
    query: "(any-hover: hover), (hover: hover), (any-pointer: fine)",
  });
  const hasTouch = useMediaQuery({ query: "(any-pointer: coarse)" });

  const mapInstance = useRef<google.maps.Map>();
  const hasFittedMap = useRef<boolean>(false);

  const [currentMap, setCurrentMap] = useState<google.maps.Map>();
  const [isRoadview, setIsRoadview] = useState<boolean>(true);

  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [isViewRecorderList, setIsViewRecorderList] = useState<boolean>(false);

  const [clusterRadius, setClusterRadius] = useState<number>(30);
  const { data: cloudStatus } = useQuery(
    ["getSpectrumCloud"],
    getSpectrumCloud,
    {
      refetchOnWindowFocus: false,
      refetchInterval: 600000,
    }
  );
  const [mapZoom, setMapZoom] = useState<number | undefined>(mapOptions.zoom);

  const [selectedAlert, setSelectedAlert] = useState<AlertViewInfo | null>(
    null
  );
  const [hoveredMarker, setHoveredMarker] = useState<MapSearchData | null>();
  const [animatedRecorders, setAnimatedRecorders] = useState<string[]>([]);

  const [isSearchFetching, setIsSearchFetching] = useState<boolean>(false);
  const isMobile = mediaQuery.useIsMobile();

  const [more, setMore] = useState<More>({
    types: false,
    recorders: false,
    alerts: false,
    recorderGroup: false,
  });

  const onSelectMoreFilter = (e: any) => {
    let {
      target: { checked, name },
    } = e;

    setMore({
      ...more,
      [name]: checked,
    });
  };

  const onDeleteMoreFilter = (name: string) => {
    if (name === "types") {
      dispatch(setTypes({ ...mapFilterInitialState.filters.types }));
    }
    if (name === "alerts") {
      dispatch(setAlerts({ ...mapFilterInitialState.filters.alerts }));
    }
    if (name === "recorders") {
      dispatch(setRecorders({ ...mapFilterInitialState.filters.recorders }));
    }
    setMore({
      ...more,
      [name]: false,
    });
  };
  const getInfoWindowContent = (marker: CustomMarker) => {
    const tempMarkerData = marker.data;
    if (!marker || !tempMarkerData) {
      return <></>;
    }

    if (tempMarkerData.groupingSystems.length === 0) {
      return <></>;
    }

    const isGrouping = checkingMergedSystem(tempMarkerData.groupingSystems);

    if (isGrouping) {
      return (
        <RecorderListInfoView
          data={tempMarkerData}
          onSelectRecorder={onSelectRecorder}
          cloudStatus={cloudStatus}
        />
      );
    }

    return (
      <RecorderInfoView
        system={tempMarkerData.groupingSystems[0]}
        onClickMarkerAlert={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClickMarkerAlert(tempMarkerData.groupingSystems);
        }}
        onSelectRecorder={onSelectRecorder}
        cloudStatus={cloudStatus}
        closeInfoWindow={closeInfoWindow}
      />
    );
  };

  const { needToForceUpdated } = useAppSelector(selectMapStomp);
  const refetchIntervalTime = oneHourInMs;

  const { data: searchData, refetch } = useQuery(
    [
      "searchMapFilterResult",
      selectedAccount.accountId,
      needToForceUpdated,
      mapFilter.filters,
      mapFilter.keyword,
      more,
    ],
    async () => {
      let tempMapFilter = { ...filteringToPayload(mapFilter.filters, more) };

      let payload: any = {
        keyword: mapFilter.keyword,
        ...tempMapFilter,
      };

      // console.log(payload);
      return postFilterSearchRecorders({
        accountId: selectedAccount.accountId,
        payload: payload,
      });
    },
    {
      retry: 0,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: true,
      refetchInterval: refetchIntervalTime,
      // staleTime: refetchIntervalTime,
      select: (newData: DashboardFilterAlertResponse) => {
        if (newData?.error === 0 && newData.result !== undefined) {
          if (
            !selectedAccount.isEnabled ||
            !selectedAccount.userPermission.viewAlerts
          ) {
            return (newData.result || []).map((item) => ({
              ...item,
              alerts: [],
              warnings: [],
              systemWarnings: 0,
              systemAlerts: 0,
              storageWarnings: 0,
              storageAlerts: 0,
              videoWarnings: 0,
              videoAlerts: 0,
            }));
          }
          return newData.result;
        }

        return [];
      },
      onSettled: () => {
        setIsSearchFetching(false);
      },
    }
  );

  useEffect(() => {
    dispatch(setMapSearchOriginData(searchData ?? []));
  }, [searchData]);

  useStompDebouncedRefetch({
    stompKey: [
      "recorderListUpdated",
      "alertListUpdated",
    ] as StompDateSignalKey[],
    refetch: refetch,
  });

  const handleActiveMarker = useCallback(
    (marker: CustomMarker) => {
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      if (!marker || !marker.data) return;

      const infoWindowDiv = document.createElement("div");

      // React 컴포넌트 마운트
      const root = createRoot(infoWindowDiv);
      root.render(
        <Provider store={store}>
          <IntlProvider locale="en" defaultLocale="en">
            <QueryClientProvider client={queryClient}>
              <ThemeProvider theme={theme.theme === "light" ? light : dark}>
                {getInfoWindowContent(marker)}
              </ThemeProvider>
            </QueryClientProvider>
          </IntlProvider>
        </Provider>
      );

      const markerInfoWindow = new google.maps.InfoWindow({
        content: infoWindowDiv,
      });

      infoWindowRef.current = markerInfoWindow;
      infoAnchorRef.current = marker;

      infoWindowRef.current.setOptions({
        pixelOffset: (marker as any).popupPosition,
        disableAutoPan: true,
      });
      infoWindowRef.current.open({
        anchor: marker,
        map: mapInstance.current,
      });
    },
    [mapInstance.current]
  );

  // 선택한 지도 marker 왼쪽 Alert View 표시 이벤트
  const onClickMarkerAlert = useCallback(
    (systems: MarkerDataSystem[]) => {
      if (
        systems !== undefined &&
        systems.length === 1 &&
        currentMapSearchData
      ) {
        let tempSelectedAlert = currentMapSearchData.find(
          (row: DashboardFilterResult) =>
            systems !== undefined && row.recorderId === systems[0].recorderId
        );

        tempSelectedAlert && tempSelectedAlert.location
          ? setSelectedAlert({
              ...tempSelectedAlert,
              recorderName: tempSelectedAlert.name,
              location: tempSelectedAlert.location.location,
            })
          : setSelectedAlert(null);
        !canHover && closeInfoWindow();
      }
    },

    [currentMapSearchData, setSelectedAlert]
  );

  const {
    infoWindowRef,
    infoAnchorRef,
    onZoomFit,
    onZoomPlus,
    onZoomOut,
    closeInfoWindow,
  } = useAdvancedMarkers({
    map: currentMap,
    clusterRadius: clusterRadius,
    handleActiveMarker,
    onClickMarkerAlert,
    animatedRecorders,
  });
  /**
   * isSearchMove (Search As Moved) 에 따른 dashboarddata 변경
   */
  // const adjustMap = useCallback(() => {
  //   if (!currentMapSearchData || currentMapSearchData.length === 0) {
  //     dispatch(setVisibleMapSearchData([]));
  //     return;
  //   }
  //   const tempData: MapSearchData[] = isSearchMove.current
  //     ? currentMapSearchData.filter((rec: MapSearchData) =>
  //         mapInstance.current
  //           ?.getBounds()
  //           ?.contains(new google.maps.LatLng(rec.latitude, rec.longitude))
  //       )
  //     : currentMapSearchData;

  //   dispatch(setVisibleMapSearchData(tempData));
  // }, [dispatch, isSearchMove, currentMapSearchData]);

  const initMap = (map: google.maps.Map) => {
    mapInstance.current = map;
    setCurrentMap(map);

    setMapZoom(map.getZoom());

    if (window.innerWidth > 3000) {
      mapInstance.current.setZoom(6);
    }
  };

  const smoothZoom = (
    max: number,
    cnt: number,
    loc?: google.maps.LatLng,
    bounds?: google.maps.LatLngBounds
  ) => {
    if (mapInstance.current !== undefined) {
      const currentZoom = cnt || (mapInstance.current.getZoom() as number);

      if (currentZoom < max) {
        google.maps.event.addListenerOnce(
          mapInstance.current,
          "zoom_changed",
          function (event: any) {
            if (loc !== undefined && mapInstance.current !== undefined) {
              mapInstance.current.setCenter(loc);
            }
            smoothZoom(max, cnt + (max > currentZoom ? 1 : -1), loc);
          }
        );

        setTimeout(function () {
          if (mapInstance.current) {
            mapInstance.current.setZoom(cnt + (max > currentZoom ? 1 : -1));
          }
        }, 200);
      }
    }
  };

  const smoothZoomOut = (min: number, cnt: number) => {
    if (cnt <= min) {
      setTimeout(function () {
        if (mapInstance.current) {
          mapInstance.current.setZoom(min);
        }
      }, 10);
      return;
    } else {
      if (mapInstance.current !== undefined) {
        let z = google.maps.event.addListenerOnce(
          mapInstance.current,
          "idle",
          function (event: any) {
            //google.maps.event.removeListener(z);
            smoothZoomOut(min, cnt - 3);
          }
        );
        // if (mapInstance.current) {
        //   mapInstance.current.setZoom(cnt);
        // };
        setTimeout(function () {
          if (mapInstance.current) {
            mapInstance.current.setZoom(cnt);
          }
        }, 80);
      }
    }
  };

  const onChangeCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  const onChangeClusterRadius = useCallback(
    (value: number) => {
      setClusterRadius(value);
    },
    [setClusterRadius]
  );

  const onChangeView = useCallback(() => {
    if (mapInstance.current !== undefined) {
      if (isRoadview) {
        mapInstance.current.setMapTypeId(google.maps.MapTypeId.SATELLITE);
      } else {
        mapInstance.current.setMapTypeId(google.maps.MapTypeId.ROADMAP);
      }
      setIsRoadview(!isRoadview);
    }
  }, [isRoadview]);

  const onSelectRecorder = (rec: MapSearchData) => {
    setSelectedAlert({
      ...rec,
      recorderName: rec.name,
      location: rec.locationAddress,
    });
    !canHover && closeInfoWindow();
  };

  const onClickRecorderKeyword = (rec: KeywordSearchRecorder) => {
    setSelectedAlert({
      ...rec,
      location: rec.location.location,
    });
  };

  const onCloseRecorderView = () => {
    setSelectedAlert(null);
  };
  const onCloseRecorderList = () => {
    onResetSearchInput();
    setIsViewRecorderList(false);
  };

  const filteringToPayload = (filter: DashboardFilter, more: More) => {
    let tempFilters: DashboardFilterRequest = { ...filter };
    let tempMores = Object.entries(more)
      .filter((mo) => mo[1] === true)
      .map((r) => r[0]);

    if (
      tempFilters.types &&
      Object.values(tempFilters.types).every((type) => !type)
    ) {
      delete tempFilters["types"];
    }
    if (
      tempFilters.status &&
      Object.values(tempFilters.status).every((status) => !status)
    ) {
      delete tempFilters["status"];
    }
    if (tempFilters.alerts) {
      let tempAlert = Object.values(tempFilters.alerts)
        .map((f) => Object.values(f).every((i) => !i))
        .some((a) => !a);

      if (!tempAlert) {
        delete tempFilters["alerts"];
      }
    }

    // more filter
    tempFilters = Object.fromEntries(
      Object.entries(tempFilters).filter(([key]) => {
        const commonConditions =
          key === "status" ||
          (isCollapsed && key === "alerts") ||
          tempMores.includes(key);

        if (selectedAccount.accountLevel === "EU") {
          return commonConditions || key === "recorders";
        } else {
          return commonConditions || key === "accounts";
        }
      })
    );
    // console.log(filter, tempFilters);

    return tempFilters;
  };

  const onResetSearchInput = () => {
    dispatch(
      setKeyword({
        type: "",
        keyword: "",
      })
    );
  };

  const onSearch = (keyword: MapFilterKeyword) => {
    setIsSearchFetching(true);
    dispatch(setKeyword(keyword));
    queryClient.invalidateQueries("searchMapFilterResult");
    setIsViewRecorderList(true);
    setSelectedAlert(null);
  };

  const onClickRecorder = (id: string) => {
    if (mapInstance.current && currentMapSearchData) {
      const selectedRecorder = currentMapSearchData.find(
        (recorder: MapSearchData) => recorder.recorderId === id
      );
      if (selectedRecorder !== undefined && selectedRecorder?.location) {
        mapInstance.current.setCenter(
          new google.maps.LatLng(
            selectedRecorder.location.latitude,
            selectedRecorder.location.longitude
          )
        );
        smoothZoom(17, mapInstance.current.getZoom() as number);
      }
    }
  };

  const handleMouseEnter = useMemo(
    () =>
      debounce((sys: MapSearchData | null) => {
        // console.log(hoveredMarker, sys, animatedRecorders);
        if (sys === null && hoveredMarker) {
          setAnimatedRecorders((prev) =>
            prev.filter((item) => item !== hoveredMarker.recorderId)
          );
        }

        setHoveredMarker(sys);
      }, 100),
    [hoveredMarker, setAnimatedRecorders, setHoveredMarker]
  );

  const onHoverSystem = useCallback(
    (sys: MapSearchData | null) => {
      handleMouseEnter(sys);
    },
    [handleMouseEnter]
  );

  const syncBounds = useMemo(
    () =>
      throttle(() => {
        const map = mapInstance.current;
        if (!map) {
          return;
        }
        const b = map.getBounds();
        if (b) {
          dispatch(setMapBounds(b));
        }
      }, 150),
    [dispatch]
  );

  useEffect(() => {
    const map = mapInstance.current;
    if (!map || !markers) return;

    // 처음 한번만 fitBounds (이유: recorder 를 보던 중 recorder 가 추가되어 마커가 생기면 fitBounds 되면서 유저가 보고있던 중심이 변경되기 때문에 한번만 fitbounds)
    if (!hasFittedMap.current && markers.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      markers.forEach((marker) =>
        bounds.extend(
          {
            lat: marker.latitude,
            lng: marker.longitude,
          }!
        )
      );
      map.fitBounds(bounds, 12);
      hasFittedMap.current = true;

      const b = map.getBounds();
      if (b) {
        dispatch(setMapBounds(b));
      }
    }

    const zoomListener = map.addListener("zoom_changed", () => {
      // syncBounds();
      setMapZoom(map.getZoom());
      closeInfoWindow();
    });

    const idleListener = map.addListener("idle", () => {
      syncBounds();
    });

    // const boundsListener = map.addListener("bounds_changed", () => {
    //   syncBounds();
    // });

    const clickListener = map.addListener("click", () => closeInfoWindow());

    const touchListener = hasTouch
      ? map.addListener("touchstart", () => closeInfoWindow())
      : null;

    return () => {
      zoomListener.remove();
      idleListener.remove();
      // boundsListener.remove();
      clickListener.remove();
      touchListener?.remove();
    };
  }, [
    mapInstance.current,
    hasFittedMap.current,
    markers,
    dispatch,
    hasTouch,
    debounce,
  ]);

  useEffect(() => {
    if (!isViewRecorderList) {
      onCloseRecorderView();
    }
  }, [isViewRecorderList]);

  useEffect(() => {
    onCloseRecorderView();
  }, [mapFilter]);

  useEffect(() => {
    let tempArr: string[] = [];
    // console.log(hoveredMarker, selectedAlert, tempArr);
    if (hoveredMarker) {
      tempArr.push(hoveredMarker.recorderId);
    }
    if (selectedAlert) {
      tempArr.push(selectedAlert.recorderId);
    }
    // console.log(tempArr);
    setAnimatedRecorders(tempArr);
  }, [hoveredMarker, selectedAlert]);

  useEffect(() => {
    return () => {
      /**
       * 1. 필터적용
       * 2. 다른 메뉴 진입 후 다시 지도 진입
       * 3. 이전에 필터 적용했던 지도 중심으로 fitBound 되었음 (hasFittedMap.current 때문)
       * 4. unmount 시 다시 초기화되도록 추가
       */
      hasFittedMap.current = false;
      dispatch(setInitialState());
      dispatch(setKeyword({ type: "", keyword: "" }));
    };
  }, []);

  return (
    <GoogleMapPresenter
      isCollapsed={isCollapsed}
      isRoadview={isRoadview}
      isViewRecorderList={isViewRecorderList}
      onChangeCollapsed={onChangeCollapsed}
      onZoomPlus={onZoomPlus}
      onZoomOut={onZoomOut}
      onZoomFit={onZoomFit}
      onChangeView={onChangeView}
      onClickRecorder={onClickRecorder}
      onSearch={onSearch}
      onResetSearchInput={onResetSearchInput}
      onCloseRecorderList={onCloseRecorderList}
      onSelectRecorder={onSelectRecorder}
      onClickRecorderKeyword={onClickRecorderKeyword}
      more={more}
      onSelectMoreFilter={onSelectMoreFilter}
      onDeleteMoreFilter={onDeleteMoreFilter}
      selectedAlert={selectedAlert}
      onCloseRecorderView={onCloseRecorderView}
      isFetching={isSearchFetching}
      initMap={initMap}
      onHoverSystem={onHoverSystem}
      onChangeClusterRadius={onChangeClusterRadius}
      clusterRadius={clusterRadius}
      cloudStatus={cloudStatus}
      mapZoom={mapZoom}
    />
  );
}
